import React from 'react';
import { Badge, BadgeVariant, BadgeSize } from './ui';

// Application status types from the shared package
export type ApplicationStatus =
  | 'received'
  | 'enriching'
  | 'enriched'
  | 'ai_processing'
  | 'ai_complete'
  | 'synced'
  | 'sr_not_found'
  | 'error'
  | 'stub'
  | 'skipped';

export interface StatusBadgeProps {
  status: ApplicationStatus;
  size?: BadgeSize;
  showDot?: boolean;
  className?: string;
  /** Set to false to hide the tooltip */
  showTooltip?: boolean;
}

interface StatusConfig {
  label: string;
  variant: BadgeVariant;
  tooltip: string;
}

const statusConfig: Record<ApplicationStatus, StatusConfig> = {
  received: {
    label: 'Received',
    variant: 'neutral',
    tooltip: 'Application received from SmartRecruiters, awaiting processing',
  },
  enriching: {
    label: 'Enriching',
    variant: 'info',
    tooltip: 'Fetching candidate resume and profile data from SmartRecruiters',
  },
  enriched: {
    label: 'Enriched',
    variant: 'info',
    tooltip: 'Candidate data loaded, ready for AI review',
  },
  ai_processing: {
    label: 'AI Processing',
    variant: 'warning',
    tooltip: 'AI is currently analyzing this candidate against job criteria',
  },
  ai_complete: {
    label: 'AI Complete',
    variant: 'success',
    tooltip: 'AI review is complete, ready to sync to SmartRecruiters',
  },
  synced: {
    label: 'Synced',
    variant: 'success',
    tooltip: 'AI review has been posted to SmartRecruiters',
  },
  sr_not_found: {
    label: 'Not in SR',
    variant: 'warning',
    tooltip: 'Candidate or application not found in SmartRecruiters',
  },
  error: {
    label: 'Error',
    variant: 'error',
    tooltip: 'An error occurred during processing - check error details',
  },
  stub: {
    label: 'Stub',
    variant: 'neutral',
    tooltip: 'Placeholder application created during sync',
  },
  skipped: {
    label: 'Skipped',
    variant: 'neutral',
    tooltip: 'Application was skipped (e.g., already reviewed by a human)',
  },
};

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  size = 'md',
  showDot = true,
  className = '',
  showTooltip = true,
}) => {
  const normalizedStatus = status.toLowerCase() as ApplicationStatus;
  const config = statusConfig[normalizedStatus] || {
    label: status,
    variant: 'neutral' as BadgeVariant,
    tooltip: status,
  };

  return (
    <Badge
      variant={config.variant}
      size={size}
      dot={showDot}
      className={className}
      title={showTooltip ? config.tooltip : undefined}
    >
      {config.label}
    </Badge>
  );
};

StatusBadge.displayName = 'StatusBadge';

// Job status badge
export type JobStatus = 'active' | 'inactive' | 'closed' | 'draft';

export interface JobStatusBadgeProps {
  status: string;
  size?: BadgeSize;
  showDot?: boolean;
  className?: string;
  /** Set to false to hide the tooltip */
  showTooltip?: boolean;
}

interface JobStatusConfig {
  label: string;
  variant: BadgeVariant;
  tooltip: string;
}

const jobStatusConfig: Record<string, JobStatusConfig> = {
  active: {
    label: 'Active',
    variant: 'success',
    tooltip: 'Job is active and candidates are being processed',
  },
  inactive: {
    label: 'Inactive',
    variant: 'neutral',
    tooltip: 'Job has been deactivated - no new candidates will be processed',
  },
  closed: {
    label: 'Closed',
    variant: 'error',
    tooltip: 'Job is closed and no longer accepting applications',
  },
  draft: {
    label: 'Draft',
    variant: 'warning',
    tooltip: 'Job is in draft mode and not yet published',
  },
  OPEN: {
    label: 'Open',
    variant: 'success',
    tooltip: 'Job is open and accepting applications',
  },
  CLOSED: {
    label: 'Closed',
    variant: 'error',
    tooltip: 'Job is closed and no longer accepting applications',
  },
  SOURCING: {
    label: 'Sourcing',
    variant: 'info',
    tooltip: 'Actively sourcing and accepting candidates',
  },
  INTERVIEW: {
    label: 'Interview',
    variant: 'info',
    tooltip: 'In the interview stage',
  },
  FILLED: {
    label: 'Filled',
    variant: 'success',
    tooltip: 'Position has been filled',
  },
  ON_HOLD: {
    label: 'On Hold',
    variant: 'warning',
    tooltip: 'Hiring is temporarily paused for this position',
  },
  CANCELLED: {
    label: 'Cancelled',
    variant: 'error',
    tooltip: 'This job requisition has been cancelled',
  },
};

export const JobStatusBadge: React.FC<JobStatusBadgeProps> = ({
  status,
  size = 'md',
  showDot = true,
  className = '',
  showTooltip = true,
}) => {
  const config = jobStatusConfig[status] || {
    label: status,
    variant: 'neutral' as BadgeVariant,
    tooltip: status,
  };

  return (
    <Badge
      variant={config.variant}
      size={size}
      dot={showDot}
      className={className}
      title={showTooltip ? config.tooltip : undefined}
    >
      {config.label}
    </Badge>
  );
};

JobStatusBadge.displayName = 'JobStatusBadge';

// Recommendation level badge
export type RecommendationLevel = 1 | 2 | 3 | 4 | 5;

export interface RecommendationBadgeProps {
  level: RecommendationLevel;
  size?: BadgeSize;
  className?: string;
  /** Set to false to hide the tooltip */
  showTooltip?: boolean;
}

interface RecommendationConfig {
  label: string;
  variant: BadgeVariant;
  tooltip: string;
}

const recommendationConfig: Record<RecommendationLevel, RecommendationConfig> = {
  1: {
    label: 'Not Recommended',
    variant: 'error',
    tooltip: 'Candidate does not meet key job requirements',
  },
  2: {
    label: 'Weak',
    variant: 'warning',
    tooltip: 'Candidate meets fewer than 40% of job criteria',
  },
  3: {
    label: 'Neutral',
    variant: 'neutral',
    tooltip: 'Candidate meets some criteria but has notable gaps',
  },
  4: {
    label: 'Good',
    variant: 'info',
    tooltip: 'Candidate meets most job criteria with minor gaps',
  },
  5: {
    label: 'Strongly Recommended',
    variant: 'success',
    tooltip: 'Candidate meets 80%+ of job criteria - strong fit',
  },
};

export const RecommendationBadge: React.FC<RecommendationBadgeProps> = ({
  level,
  size = 'md',
  className = '',
  showTooltip = true,
}) => {
  const config = recommendationConfig[level];

  return (
    <Badge
      variant={config.variant}
      size={size}
      className={className}
      title={showTooltip ? config.tooltip : undefined}
    >
      {config.label}
    </Badge>
  );
};

RecommendationBadge.displayName = 'RecommendationBadge';
